import { parse as parseSDF } from 'sdf-parser';
import { PropApplicability } from 'BMapsSrc/model/ScopedPropertyGroup';
import { makeOneSDF } from 'BMapsUtil/mol_format_utils';
import { updateLocalJobState } from 'BMapsSrc/redux/jobs/access';
import { deepFreeze } from 'BMapsUtil/js_utils';
import { JobDefinition, JobResultDestinations } from '../JobDefinition';

/**
 * The properties DrugFlow contributes to a compound, registered on the workspace by
 * `getJobsPropertyMetadata`. This is the single source of truth for their labels and
 * descriptions: `getDataInfo` derives the results-table columns from it, so the results table,
 * the compound table's tooltips and column picker, and the CSV/SDF exports all name these the
 * same way. Declaring only `getDataInfo` (as this job used to) leaves the compound table with
 * no metadata at all once the properties are imported -- no tooltip, and an export falling back
 * to a raw name (`targetName` in CSV, `drugflow/targetName` in SDF). See BP2-2736.
 */
const scopedPropertyMetadata = deepFreeze({
    compound: {
        childGroups: {
            drugflow: {
                metadata: {
                    label: 'DrugFlow properties',
                    tooltipHeading: 'DrugFlow properties',
                },
                properties: {
                    SMILES: {
                        metadata: {
                            label: 'Structure',
                            description: 'The generated molecule. Hover over it to enlarge it.',
                            structureValue: true,
                            defaultVisible: true,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    smilesText: {
                        metadata: {
                            label: 'SMILES String',
                            description: 'The generated molecule, as plain-text SMILES.',
                            type: 'string',
                            defaultVisible: false,
                            excludeFromImport: true,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    targetName: {
                        metadata: {
                            label: 'Target',
                            description: 'The target structure whose binding pocket these compounds were generated for. Their 3D poses are specific to it.',
                            type: 'string',
                            defaultVisible: true,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                    pocketLigand: {
                        metadata: {
                            label: 'Pocket Ligand',
                            description: 'The reference ligand that defined the binding pocket for generation.',
                            type: 'string',
                            defaultVisible: true,
                            propApplicability: PropApplicability.PERCOMPOUND,
                        },
                    },
                },
            },
        },
    },
});

// DrugFlow seeds `np.random.seed` directly with whatever it's given (see
// `utils.set_deterministic` in the pinned upstream source), which requires
// a value in [0, 2**32 - 1] — not an arbitrary UI choice, the actual hard
// limit of that call.
const MAX_SEED = 2 ** 32 - 1;

function randomSeed() {
    return Math.floor(Math.random() * (MAX_SEED + 1));
}

/**
 * DrugFlow structure-based de novo generation job: parses the generated
 * molecules from the result SDF into compound structures.
 *
 * Like GninaDockingJob, results are SDF molblocks (no SMILES column), so we
 * parse with sdf-parser and carry each molblock as the structure. SMILES is
 * derived on import when available.
 *
 * Owns `jobForm` (the field schema consumed by `JobForm`/`FieldList`) and
 * the submission methods below alongside its results/dispatch logic — data
 * and plain logic only, no rendering; `JobForm`/`FieldList` own all JSX.
 *
 * @augments JobDefinition
 */
export class DrugFlowJob extends JobDefinition {
    static jobTypeId = 'drugflow';
    static resultDestination = JobResultDestinations.COMPOUND;
    static jobTypeName = 'DrugFlow';
    static jobName = 'DrugFlow Job';
    static jobDescription = 'designs new molecules within the binding pocket defined by your reference ligand.\n'
        + 'Results include 3D poses specific to that pocket and target. Results are not optimized for drug-likeness.';

    static jobCitation = 'https://doi.org/10.48550/arXiv.2508.17815';

    /** The reference ligand defines a pocket, which needs a protein structure. */
    static requiresLoadedProtein = true;

    static scopedPropertyMetadata = scopedPropertyMetadata;

    /** Results rows keyed by their generated id. */
    static idField = 'id';

    /**
     * Declarative description of this job's form — data only, no rendering.
     * `body` is one of the named content regions `JobForm` renders (see
     * `formHelpers.js` for why they're named rather than an ordered list).
     * This job declares no `header`, and no submit label/tooltip/title —
     * the embedding tab supplies all of that (see `JobForm.jsx`'s
     * `mergeRegion` and its `footer` prop), since this job has no business
     * knowing what a program picker or job-naming field even are, or what
     * a given tab wants its submit button to say. `refLigandSpec` uses the
     * `pocket` widget — conceptually a compound, a hotspot, or the current
     * atom selection, unified the same way `DockingReference` in
     * `data_tools.js` already does elsewhere in the app, though today's
     * `pocket` widget only offers loaded compounds.
     * @type {object}
     */
    static jobForm = {
        body: {
            title: 'Specify Compound Input',
            fields: {
                refLigandSpec: {
                    widget: 'pocket',
                    label: 'Reference Ligand (defines pocket)',
                    default: '',
                    required: true,
                    requiredMessage: 'Select a reference ligand for DrugFlow generation',
                    // Matches REINVENT's `molDataText` (see ReinventJob.js): pre-fill from
                    // whatever compound the user acted on before opening this tab (right-click
                    // menu, System Selector), the same `initData.selectedCompounds` those
                    // already populate. It's always already loaded (both call sites act on a
                    // loaded compound), so its resSpec is guaranteed to be among the `pocket`
                    // widget's own options.
                    initialValueFrom: (initData) => initData?.selectedCompounds?.[0]?.resSpec,
                },
                nSamples: {
                    widget: 'number', label: 'Number of molecules', default: 10, min: 1,
                },
            },
            subsections: [
                {
                    title: 'Advanced Parameters',
                    collapsible: true,
                    fields: {
                        // A `variant`: which of these three kinds of value
                        // molecule_size holds determines what (if anything)
                        // else needs filling in. `fixed`/`range` each list
                        // their own sub-fields the same way this section's
                        // `fields` do. DrugFlow itself wants one string (a
                        // bare count or a "min,max" range), not the mode +
                        // sub-field(s) this variant produces — collapsing
                        // those into that one string happens explicitly in
                        // `buildRequestPayload`, since `deriveFormValues`
                        // maps one descriptor to one result key and can't
                        // combine several into one.
                        molecule_size: {
                            widget: 'variant',
                            label: 'Molecule size',
                            tooltip: 'How many heavy (non-hydrogen) atoms each generated molecule has — sampled automatically by the model, fixed to an exact size, or constrained to a size range.',
                            default: 'sampled',
                            options: {
                                sampled: { label: 'Default' },
                                fixed: {
                                    label: 'Fixed size',
                                    fields: {
                                        fixedSize: { widget: 'number', label: 'Size (heavy atoms)', min: 1 },
                                    },
                                },
                                range: {
                                    label: 'Size range',
                                    fields: {
                                        minSize: { widget: 'number', label: 'Min size (heavy atoms)', min: 1 },
                                        maxSize: { widget: 'number', label: 'Max size (heavy atoms)', min: 1 },
                                    },
                                },
                            },
                        },
                        n_steps: {
                            widget: 'number',
                            // Kept short deliberately -- `label` also gets
                            // reused verbatim in generated required/bounds
                            // messages (see `formHelpers.js`'s
                            // `boundsMessage`), so a parenthetical aside
                            // here would show up mid-error-message too
                            // (e.g. "...(blank = model default) must be at
                            // least 1" reads like a contradiction). The
                            // aside belongs in the tooltip instead.
                            label: 'Denoising steps',
                            tooltip: 'Number of denoising steps the diffusion model takes to generate each molecule. More steps can improve sample quality at the cost of runtime; the released model was evaluated with 500. Leave blank to use the model\'s own default.',
                            min: 1,
                        },
                        seed: {
                            widget: 'number',
                            // See `n_steps` above for why this stays short.
                            label: 'Random seed',
                            tooltip: 'Random seed controlling generation stochasticity — set a fixed value for reproducible results, or leave blank for a new random seed each time.',
                            // Matches numpy's `np.random.seed` range (what
                            // DrugFlow itself seeds with) — the actual hard
                            // limit, not an arbitrary UI choice.
                            min: 0,
                            max: MAX_SEED,
                            // Deliberately no `fallback`: unlike batch_size/
                            // pocket_distance_cutoff below, there's no single
                            // number blank actually resolves to — it means a
                            // fresh `randomSeed()` every submission (see
                            // `buildRequestPayload`), so showing any one
                            // number as a placeholder would misstate that.
                        },
                        batch_size: {
                            widget: 'number',
                            label: 'Batch size',
                            tooltip: 'Number of molecules generated per batch. Higher values can improve throughput at the cost of memory use.',
                            min: 1,
                            default: 32,
                            fallback: 32,
                        },
                        pocket_distance_cutoff: {
                            widget: 'number',
                            label: 'Pocket distance cutoff (Å)',
                            tooltip: 'Maximum distance (Å) from the reference ligand used to define the binding-pocket residues considered during generation.',
                            min: 1,
                            default: 8.0,
                            fallback: 8.0,
                        },
                    },
                },
            ],
        },
    };

    /**
     * Assembles the DrugFlow request payload from `payload` — the flat
     * `property: value` object `JobForm` derives from `formState` via
     * `deriveFormValues` (already resolved and stripped of which
     * section/subsection a property came from — this method never has to
     * know the "Advanced Parameters" subsection existed). Every numeric
     * field here (`nSamples`, `fixedSize`/`minSize`/`maxSize`, `seed`, and
     * whatever rides through in `...rest` — `n_steps`, `batch_size`,
     * `pocket_distance_cutoff`) already arrives as an actual number, not a
     * string: `NumberWidget` (`widgets/simpleWidgets.jsx`) casts at the
     * source, in its own `setValue`, rather than leaving every consumer of
     * a number field to re-cast defensively — so this method needs no
     * `Number()` calls of its own. `refLigandSpec` arrives already resolved
     * (by the `pocket` widget's resolver in `widgets/index.js`) into
     * `{ proteinPdb, ligandSdf, targetName, pocketLigand }` (the last two
     * only name the pocket's source, and become the job's stash).
     * `molecule_size` is the one thing this
     * method can't get generically at all: DrugFlow wants one string (a
     * bare count or a "min,max" range), not the separate mode + sub-
     * field(s) the `variant` widget produces, so collapsing those into
     * that one string happens explicitly here. `seed` is the other
     * exception: when the field is left blank, `payload.seed` is simply
     * absent (blank fields never reach `payload` at all — see
     * `formHelpers.js`'s `isEmpty` check), and omitting `--seed` entirely
     * doesn't give DrugFlow a random run — the pinned upstream script's own
     * `argparse` default (`--seed`, `default=42`) silently takes over, so
     * "leave it blank" would otherwise always reproduce the same run. A
     * fresh `randomSeed()` fills that gap so blank actually means random,
     * matching the field's own tooltip.
     * @param {object} payload
     * @returns {Promise<{ requestData?: object, error?: string[] }>}
     */
    static async buildRequestPayload(payload) {
        const {
            refLigandSpec: {
                proteinPdb, ligandSdf, targetName, pocketLigand,
            },
            nSamples, molecule_size: moleculeSizeMode, fixedSize, minSize, maxSize, seed,
            ...rest
        } = payload;

        // fixedSize/minSize/maxSize are the `molecule_size` variant's own
        // sub-fields, not top-level jobForm descriptors — they never pass
        // through formHelpers.js's generic min/max check (it only walks the
        // flat descriptor list), so their `min: 1` has to be enforced here.
        // `Number.isFinite` rather than bare falsiness or an `=== undefined`
        // check specifically: falsiness would treat a legitimate, explicitly-
        // typed 0 as if the field were empty (misreporting "Enter a fixed
        // molecule size" instead of "must be at least 1 heavy atom" — the same
        // falsy-zero trap `deriveFormValues`'s own `isEmpty` exists to avoid
        // one level up); `=== undefined` alone would fix that but still let
        // NaN/Infinity (e.g. typing `1e400`, a syntactically valid number
        // literal) sneak past both this check AND the `< 1` check below
        // (`NaN < 1` and `Infinity < 1` are both `false`), silently landing
        // `String(Infinity)`/`String(NaN)` in the request with no error
        // surfaced at all. `Number.isFinite` rejects undefined, NaN, and
        // Infinity in one check.
        let moleculeSize;
        if (moleculeSizeMode === 'fixed') {
            if (!Number.isFinite(fixedSize)) return { error: 'Enter a fixed molecule size.' };
            if (fixedSize < 1) return { error: 'Molecule size must be at least 1 heavy atom.' };
            moleculeSize = String(fixedSize);
        } else if (moleculeSizeMode === 'range') {
            if (!Number.isFinite(minSize) || !Number.isFinite(maxSize)) {
                return { error: 'Enter both minimum and maximum molecule sizes.' };
            }
            if (minSize < 1 || maxSize < 1) {
                return { error: 'Molecule size must be at least 1 heavy atom.' };
            }
            if (minSize > maxSize) {
                return { error: 'Minimum molecule size cannot exceed maximum molecule size.' };
            }
            moleculeSize = `${minSize},${maxSize}`;
        }

        const requestData = {
            protein_pdb: proteinPdb,
            ref_ligand_sdf: ligandSdf,
            n_samples: nSamples,
            seed: seed ?? randomSeed(),
            ...rest,
            ...(moleculeSize !== undefined ? { molecule_size: moleculeSize } : {}),
        };

        // Which target and reference ligand the pocket came from. This describes the job rather
        // than feeding the computation, so it stays out of `requestData` (the container's input)
        // and rides along as the job's stash, which `getRequestMeta` records.
        return { requestData, stash: { targetName, pocketLigand } };
    }

    /**
     * Records which target and reference ligand defined the pocket, so results can show what
     * their poses are specific to. Comes from the submission stash (see `buildRequestPayload`).
     * @override
     * @param {object} jobrequest
     * @param {object} [stash]
     * @returns {object}
     */
    getRequestMeta(jobrequest, stash = {}) {
        return {
            jobName: jobrequest?.jobName,
            targetName: stash?.targetName,
            pocketLigand: stash?.pocketLigand,
        };
    }

    /**
     * Results-table columns for generated compounds.
     * @override
     * @returns {Object<string, import('../JobDefinition').DataInfoEntry>}
     */
    static getDataInfo() {
        return {
            // Columns and their labels come from `scopedPropertyMetadata` above, so the table
            // and the compound's imported properties can't drift apart.
            ...super.getDataInfo(),
            // `sdf` is temporarily 3D-only: today the sole consumer is the results tab's
            // "Download 3D SDF", and only the job types returning 3D poses populate it. The name
            // is the general format, so resist reading it as "the SDF for this row" -- a 2D job
            // type's rows would need their own source rather than this key.
            // Row plumbing, not results: without these the grid adds raw `id` and `sdf` columns.
            id: { excludeFromTable: true },
            sdf: { excludeFromTable: true, excludeFromImport: true },
        };
    }

    /**
     * Parses the DrugFlow SDF into generated structures and stores them on
     * local job state for the results table / import.
     * @override
     * @param {object} job
     * @param {object|string} results
     * @param {import('../JobDefinition').IntegrateResultsOptions} [_options]
     * @returns {Promise<import('../JobDefinition').IntegrateResultsReturn>}
     */
    async integrateResults(job, results, _options) {
        let resultsPacket = results;
        let structures = [];
        let error;
        try {
            if (!resultsPacket) return { error: 'Empty results' };
            if (typeof resultsPacket === 'string') resultsPacket = JSON.parse(resultsPacket);
            if (!resultsPacket.jobResults?.sdf) return { error: 'Empty results' };
            const parsed = parseSDF(resultsPacket.jobResults.sdf);
            structures = parsed.molecules.map((entry, i) => ({
                id: `${job.jobid}_${i}`,
                SMILES: entry.smiles || null,
                smilesText: entry.smiles || null,
                // Absent on jobs submitted before this was recorded.
                targetName: job.requestmeta?.targetName ?? null,
                pocketLigand: job.requestmeta?.pocketLigand ?? null,
                sdf: makeOneSDF(entry.molfile),
            }));
            updateLocalJobState(job.jobid, { structures });
        } catch (ex) {
            error = `Error loading ${this.jobTypeId} results`;
            console.error(error, ex);
        }
        return {
            error,
            results: { structures, jobResults: resultsPacket.jobResults },
        };
    }
}
